import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  QrCode, Package, Clock, CheckCircle2, Camera, AlertCircle,
  Search, RefreshCw, ChevronRight, Wrench, Upload
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TechnicianPanelPage = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanMode, setScanMode] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scannedWarranty, setScannedWarranty] = useState(null);
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [updating, setUpdating] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/technician/queue`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setQueue(data.queue || []);
      } else if (res.status === 401) {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScanQR = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/technician/scan/${code}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setScannedWarranty(data);
        setSelectedWarranty(data);
        setScanMode(false);
        toast.success(`Encontrado: ${data.product_name}`);
      } else {
        toast.error('No se encontró solicitud con este código');
      }
    } catch (error) {
      toast.error('Error al buscar');
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScanQR(manualCode.trim());
    }
  };

  const updateStatus = async (requestId, newStatus, notes = '') => {
    setUpdating(true);
    try {
      const url = new URL(`${API_URL}/api/technician/warranty/${requestId}/status`);
      url.searchParams.append('status', newStatus);
      if (notes) url.searchParams.append('notes', notes);
      
      const res = await fetch(url, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(`Estado actualizado: ${newStatus}`);
        if (selectedWarranty?.request_id === requestId) {
          setSelectedWarranty({ ...selectedWarranty, status: newStatus });
        }
        fetchQueue();
      } else {
        toast.error('Error al actualizar estado');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoUpload = async (requestId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // In production, upload to cloud storage first
    // For now, we'll use a placeholder URL
    const photoUrl = URL.createObjectURL(file);
    
    try {
      const res = await fetch(`${API_URL}/api/technician/warranty/${requestId}/photo?photo_url=${encodeURIComponent(photoUrl)}&description=Foto de reparación`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Foto añadida');
      } else {
        toast.error('Error al subir foto');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const statusColors = {
    'Recibido en Servicio Técnico': 'bg-blue-100 text-blue-700',
    'En Diagnóstico': 'bg-purple-100 text-purple-700',
    'En Reparación': 'bg-yellow-100 text-yellow-700',
    'Pendiente de Piezas': 'bg-orange-100 text-orange-700',
    'Reparación Completada': 'bg-green-100 text-green-700',
    'En Control de Calidad': 'bg-teal-100 text-teal-700'
  };

  const statusOptions = [
    'Recibido en Servicio Técnico',
    'En Diagnóstico',
    'En Reparación',
    'Pendiente de Piezas',
    'Reparación Completada',
    'En Control de Calidad'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="technician-panel">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wrench className="w-8 h-8 text-[#0047FF]" />
              <div>
                <h1 className="text-xl font-bold">Panel de Técnico</h1>
                <p className="text-sm text-gray-400">{queue.length} en cola</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setScanMode(true)}
                className="bg-[#0047FF] hover:bg-[#003BCC]"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Escanear QR
              </Button>
              <Button
                onClick={fetchQueue}
                variant="outline"
                className="border-gray-600 text-white hover:bg-gray-700"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Queue List */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-lg font-semibold mb-4">Cola de Reparaciones</h2>
            
            {queue.length === 0 ? (
              <div className="text-center py-16 bg-gray-800 rounded-xl">
                <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay reparaciones pendientes</p>
              </div>
            ) : (
              queue.map((item) => (
                <motion.div
                  key={item.request_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-gray-800 rounded-xl p-4 border cursor-pointer transition-all ${
                    selectedWarranty?.request_id === item.request_id
                      ? 'border-[#0047FF]'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                  onClick={() => setSelectedWarranty(item)}
                  data-testid={`queue-item-${item.request_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{item.product_name || 'Sin nombre'}</p>
                      <p className="text-sm text-gray-400">{item.request_id}</p>
                      <p className="text-sm text-gray-500 mt-1">{item.problem_description?.slice(0, 80)}...</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${statusColors[item.status] || 'bg-gray-600'}`}>
                      {item.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(item.created_at).toLocaleDateString('es-ES')}
                    </span>
                    {item.tracking_number && (
                      <span>Tracking: {item.tracking_number}</span>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedWarranty ? (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 sticky top-24">
                <h3 className="text-lg font-semibold mb-4">Detalles</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-400">Producto</p>
                    <p className="font-medium">{selectedWarranty.product_name}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-400">ID Solicitud</p>
                    <p className="font-mono text-sm">{selectedWarranty.request_id}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-400">Problema</p>
                    <p className="text-sm">{selectedWarranty.problem_description}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-400">Estado actual</p>
                    <span className={`inline-block text-xs px-3 py-1 rounded-full mt-1 ${statusColors[selectedWarranty.status] || 'bg-gray-600'}`}>
                      {selectedWarranty.status}
                    </span>
                  </div>
                  
                  {/* Quick Status Update */}
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Actualizar estado</p>
                    <div className="grid grid-cols-2 gap-2">
                      {statusOptions.map((status) => (
                        <button
                          key={status}
                          onClick={() => updateStatus(selectedWarranty.request_id, status)}
                          disabled={updating || selectedWarranty.status === status}
                          className={`text-xs py-2 px-3 rounded-lg border transition-colors ${
                            selectedWarranty.status === status
                              ? 'bg-[#0047FF] border-[#0047FF] text-white'
                              : 'border-gray-600 hover:border-[#0047FF] hover:bg-[#0047FF]/10'
                          } disabled:opacity-50`}
                        >
                          {status.replace('Recibido en Servicio Técnico', 'Recibido')}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Photo Upload */}
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Añadir foto</p>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      ref={fileInputRef}
                      onChange={(e) => handlePhotoUpload(selectedWarranty.request_id, e)}
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="w-full border-gray-600 text-white hover:bg-gray-700"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Subir foto de reparación
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 text-center">
                <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">
                  Selecciona una solicitud o escanea un QR
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scan Modal */}
      {scanMode && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-2xl p-6 max-w-md w-full"
          >
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <QrCode className="w-6 h-6" />
              Escanear/Buscar Solicitud
            </h2>
            
            {/* Camera would go here in a real implementation */}
            <div className="bg-gray-900 rounded-xl h-48 flex items-center justify-center mb-4">
              <div className="text-center text-gray-500">
                <QrCode className="w-16 h-16 mx-auto mb-2" />
                <p className="text-sm">Cámara QR no disponible en web</p>
                <p className="text-xs">Usa búsqueda manual</p>
              </div>
            </div>
            
            {/* Manual Search */}
            <form onSubmit={handleManualSearch} className="flex gap-2 mb-4">
              <Input
                placeholder="Introduce ID de solicitud..."
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                className="bg-gray-700 border-gray-600"
              />
              <Button type="submit" className="bg-[#0047FF]">
                <Search className="w-4 h-4" />
              </Button>
            </form>
            
            <Button
              onClick={() => setScanMode(false)}
              variant="outline"
              className="w-full border-gray-600 text-white hover:bg-gray-700"
            >
              Cancelar
            </Button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TechnicianPanelPage;
